import axios from "./axios";

export type NotificationItem = {
  _id: string;
  type: string;
  title: string;
  message: string;
  status?: string;
  priority?: "info" | "warning" | "critical";
  route?: {
    path?: string;
    campaignId?: string;
    listId?: string;
  } | null;
  entityType?: string;
  entityId?: string | null;
  metadata?: any;
  readAt?: string | null;
  dismissedAt?: string | null;
  createdAt: string;
};

export function getNotificationCampaignName(item: NotificationItem): string {
  const metadata = item?.metadata || {};
  const campaignName = String(
    metadata?.campaignName ||
      metadata?.campaign?.name ||
      metadata?.campaign?.campaignName ||
      ""
  ).trim();
  return campaignName;
}

function normalizeText(value?: string | null) {
  return String(value || "").trim().toLowerCase();
}

function toSentence(value?: string | null) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function normalizeNotification(item: NotificationItem): NotificationItem {
  const title = String(item.title || "").trim();
  const message = String(item.message || "").trim();
  const status = normalizeText(item.status || item.metadata?.activityStatus);
  const titleLower = title.toLowerCase();
  const messageLower = message.toLowerCase();

  const isFailureMessage =
    messageLower.includes("failed") || messageLower.includes("error");
  const isSuccessMessage =
    messageLower.includes("successful") || messageLower.includes("success");

  let nextTitle = title;
  if (status === "fail" || isFailureMessage) {
    if (titleLower.includes("successful") || titleLower.includes("success")) {
      nextTitle = toSentence(message) || title.replace(/successful|success/gi, "failed");
    }
  } else if (status === "success" || isSuccessMessage) {
    if (titleLower.includes("failed") || titleLower.includes("error")) {
      nextTitle = toSentence(message) || title.replace(/failed|error/gi, "successful");
    }
  }

  return {
    ...item,
    title: nextTitle || item.title,
    message: message || nextTitle || item.message,
    status: status || item.status,
  };
}

function resolveScope() {
  const userRaw = localStorage.getItem("user");
  const wsRaw = localStorage.getItem("currentWorkSpace");
  const ownerRaw = localStorage.getItem("owner");
  const user = userRaw ? JSON.parse(userRaw) : null;
  const ws = wsRaw ? JSON.parse(wsRaw) : null;
  const owner = ownerRaw ? JSON.parse(ownerRaw) : null;

  return {
    userId: user?._id || user?.userId || owner?.userId || "",
    workspaceId: ws?._id || "",
  };
}

export async function fetchNotifications(options?: { limit?: number; offset?: number }) {
  const scope = resolveScope();
  const { data } = await axios.post("/notifications/list", {
    ...scope,
    limit: options?.limit ?? 25,
    offset: options?.offset ?? 0,
  });
  const typed = data as { items: NotificationItem[]; total: number; limit: number; offset: number };
  return {
    ...typed,
    items: Array.isArray(typed.items) ? typed.items.map(normalizeNotification) : [],
  };
}

export async function fetchUnreadCount() {
  const scope = resolveScope();
  const { data } = await axios.post("/notifications/unreadCount", scope);
  return data as { unread: number };
}

export async function markNotificationRead(notificationId: string) {
  const scope = resolveScope();
  const { data } = await axios.post("/notifications/markRead", { ...scope, notificationId });
  return data as { success: boolean };
}

export async function markAllNotificationsRead() {
  const scope = resolveScope();
  const { data } = await axios.post("/notifications/markAllRead", scope);
  return data as { success: boolean; updated: number };
}

export async function dismissNotification(notificationId: string) {
  const scope = resolveScope();
  const { data } = await axios.post("/notifications/dismiss", { ...scope, notificationId });
  return data as { success: boolean };
}

